import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const LOCALES = ["en", "fr", "nl"] as const;

const ALL_VALUE = "__ALL__";

type Entity = "questions" | "survey-templates";

interface CSVExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entity: Entity;
}

export const CSVExportDialog = ({ isOpen, onClose, entity }: CSVExportDialogProps) => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Array<{ id: string; name: string }>>([]);
  const [tools, setTools] = useState<Array<{ id: string; name: string }>>([]);

  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [selectedTool, setSelectedTool] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      if (entity === "questions") {
        const [{ data: tData }, { data: toolsData }] = await Promise.all([
          supabase.from("survey_templates").select("id, name").order("name"),
          supabase.from("tools").select("id, name").order("name"),
        ]);
        setTemplates(tData || []);
        setTools(toolsData || []);
      } else {
        const { data: toolsData } = await supabase.from("tools").select("id, name").order("name");
        setTools(toolsData || []);
      }
    };
    load();
  }, [isOpen, entity]);

  const docs = useMemo(() => {
    if (entity === "questions") {
      return (
        <div className="space-y-3 text-sm">
          <p>Export will include:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>id, is_required, order_index, tool_id, question_type_id</li>
            <li>question_text_{LOCALES.join(", question_text_")}</li>
            <li>tool_name, question_type_name, focus_area_names</li>
            <li>survey_template_name and template_order_index when filtered by a template</li>
          </ul>
        </div>
      );
    }
    return (
      <div className="space-y-3 text-sm">
        <p>Export will include all survey template fields and localized names/descriptions.</p>
      </div>
    );
  }, [entity]);

  const runExport = async () => {
    setBusy(true);
    try {
      const fn = entity === "questions" ? "export-questions" : "export-templates";
      const { data, error } = await supabase.functions.invoke(fn, {
        body: {
          survey_template_id: selectedTemplate || undefined,
          tool_id: selectedTool || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      if (error) throw error;

      const csv: string = data?.csv || "";
      const filename: string = data?.filename || `${entity}.csv`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Export ready", description: `Downloaded ${filename}` });
      onClose();
    } catch (e: any) {
      console.error(e);
      toast({ title: "Export failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o ? onClose() : null}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Export {entity === "questions" ? "Questions" : "Survey Templates"}</DialogTitle>
          <DialogDescription>Choose optional filters then export to CSV.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="filters" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="filters">Filters</TabsTrigger>
            <TabsTrigger value="docs">Docs</TabsTrigger>
          </TabsList>

          <TabsContent value="filters" className="space-y-4 mt-4">
            {entity === "questions" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Survey Template (optional)</Label>
                  <Select value={selectedTemplate} onValueChange={(v) => setSelectedTemplate(v === ALL_VALUE ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All templates" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_VALUE}>All templates</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tool (optional)</Label>
                  <Select value={selectedTool} onValueChange={(v) => setSelectedTool(v === ALL_VALUE ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All tools" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_VALUE}>All tools</SelectItem>
                      {tools.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {entity === "survey-templates" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tool (optional)</Label>
                  <Select value={selectedTool} onValueChange={(v) => setSelectedTool(v === ALL_VALUE ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All tools" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_VALUE}>All tools</SelectItem>
                      {tools.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date from (optional)</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Date to (optional)</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>

            <Button onClick={runExport} disabled={busy}>{busy ? "Exporting..." : "Export CSV"}</Button>
          </TabsContent>

          <TabsContent value="docs" className="mt-4">
            {docs}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
